import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import type {
  TerritorialManagerListItem,
  TerritorialManagerPartner,
  TerritorialManagerPeriodTotals,
  TerritorialManagerPartnerPeriodDetailRow,
  TerritorialManagerWeeklyRevenueRow,
  PartnerCoverageRow,
  PartnerManagerHistoryRow,
  TerritorialManagerClosedAssignment,
  McUserForTuAdmin,
} from '@/types'

export type StudioWeeklyPlanFactRow = {
  studio_internal_id: number
  period_id: number
  week_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  weekly_gross_revenue_plan: number | null
  weekly_gross_revenue_fact: number | null
  weekly_marketing_budget_plan: number | null
  weekly_marketing_expenses_amount_fact: number | null
  weekly_leads_plan: number | null
  weekly_leads_fact: number | null
  weekly_appointments_plan: number | null
  weekly_appointments_fact: number | null
  weekly_new_visitors_plan: number | null
  weekly_new_visitors_fact: number | null
  weekly_new_sales_plan: number | null
  weekly_new_sales_fact: number | null
  weekly_new_sales_amount_plan: number | null
  weekly_new_sales_amount_fact: number | null
  weekly_renewals_count_plan: number | null
  weekly_renewals_count_fact: number | null
  weekly_renewals_amount_plan: number | null
  weekly_renewals_amount_fact: number | null
  weekly_upgrades_count_plan: number | null
  weekly_upgrades_count_fact: number | null
  weekly_upgrades_amount_plan: number | null
  weekly_upgrades_amount_fact: number | null
  weekly_returning_clients_count_plan: number | null
  weekly_returning_clients_count_fact: number | null
  weekly_returning_clients_amount_plan: number | null
  weekly_returning_clients_amount_fact: number | null
  weekly_membership_fees_new_plan: number | null
  weekly_membership_fees_new_amount_fact: number | null
  weekly_membership_fees_subscription_upgrades_plan: number | null
  weekly_membership_fees_subscription_returning_plan: number | null
  weekly_membership_fees_subscription_upgrades_amount_fact: number | null
  weekly_membership_fees_subscription_returning_amount_fact: number | null
  weekly_new_subscriptions_count_plan: number | null
  weekly_new_subscriptions_count_fact: number | null
  weekly_new_subscriptions_amount_plan: number | null
  weekly_new_subscriptions_amount_fact: number | null
  weekly_subscription_renewals_count_plan: number | null
  weekly_subscription_renewals_count_fact: number | null
  weekly_subscription_renewals_amount_plan: number | null
  weekly_subscription_renewals_amount_fact: number | null
  weekly_subscription_upgrades_count_plan: number | null
  weekly_subscription_upgrades_count_fact: number | null
  weekly_subscription_upgrades_amount_plan: number | null
  weekly_subscription_upgrades_amount_fact: number | null
  weekly_subscription_returning_clients_count_plan: number | null
  weekly_subscription_returning_clients_count_fact: number | null
  weekly_subscription_returning_clients_amount_plan: number | null
  weekly_subscription_returning_clients_amount_fact: number | null
  weekly_refunds_amount_plan: number | null
  weekly_refunds_amount_fact: number | null
}

export type StudioDailyCallsRow = {
  studio_internal_id: number
  metric_date: string
  calls_count: number | null
  answered_calls_count: number | null
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    // P0001 — отказ, сформулированный самой RPC для человека: печатаем текст и подсказку.
    if (e.code === 'P0001' && e.message) return [e.message, e.hint].filter(Boolean).join(' ')
    // Последний барьер, если пересечение не поймала проверка в RPC (tma_assert_no_overlap).
    if (e.code === '23P01' && e.message?.includes('tma_no_overlap_per_partner')) {
      const partner = e.details?.match(/=\((\d+),/)?.[1]
      return `У франчайзи${partner ? ` (${partner})` : ''} уже есть назначение ТУ, пересекающееся с выбранной датой. `
        + 'Обновите страницу и проверьте его ТУ, либо выберите дату начала позже.'
    }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

export async function listTerritorialManagers(): Promise<TerritorialManagerListItem[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_territorial_managers')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as TerritorialManagerListItem[]
}

// Same as listTerritorialManagers(), but excludes studios with status_code = SERVICE
// from studios_count and from the dashboard aggregates on /management/territorial-managers.
export async function listTerritorialManagersNoService(): Promise<TerritorialManagerListItem[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_territorial_managers_no_service')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as TerritorialManagerListItem[]
}

export async function getManagerPartners(managerUserId: string): Promise<TerritorialManagerPartner[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_manager_partners', { p_manager_user_id: managerUserId })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as TerritorialManagerPartner[]
}

export async function listPartnerCoverage(): Promise<PartnerCoverageRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_partner_coverage')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as PartnerCoverageRow[]
}

export async function getPartnerManagerHistory(partnerNumber: number): Promise<PartnerManagerHistoryRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_partner_manager_history', { p_partner_number: partnerNumber })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as PartnerManagerHistoryRow[]
}

export async function getPartnerCurrentManager(partnerNumber: number): Promise<string | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_partner_current_manager', { p_partner_number: partnerNumber })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? null) as string | null
}

export async function getManagerPeriodTotals(managerUserId: string): Promise<TerritorialManagerPeriodTotals[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('territorial_manager_period_totals')
      .select('*')
      .eq('manager_user_id', managerUserId)
      .order('period_start', { ascending: false })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as unknown as TerritorialManagerPeriodTotals[]
}

// Period totals for ALL managers. Used by the aggregate TU dashboard on
// /management/territorial-managers. Single query — view aggregates per
// (manager, period) so the result set scales with TU count, not partners.
export async function listAllManagerPeriodTotals(): Promise<TerritorialManagerPeriodTotals[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('territorial_manager_period_totals')
      .select('*')
      .order('period_start', { ascending: false })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as unknown as TerritorialManagerPeriodTotals[]
}

// Period totals for ALL managers, excluding studios with status_code = SERVICE.
// Used only on /management/territorial-managers.
export async function listAllManagerPeriodTotalsNoService(): Promise<TerritorialManagerPeriodTotals[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('territorial_manager_period_totals_no_service')
      .select('*')
      .order('period_start', { ascending: false })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as unknown as TerritorialManagerPeriodTotals[]
}

export async function getManagerPartnerPeriodDetails(
  managerUserId: string,
  periodId?: number | null
): Promise<TerritorialManagerPartnerPeriodDetailRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_manager_partner_period_details', {
      p_manager_user_id: managerUserId,
      p_period_id: periodId ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as TerritorialManagerPartnerPeriodDetailRow[]
}

export async function getManagerPartnerWeeklyRevenue(
  managerUserId: string,
  periodId?: number | null
): Promise<TerritorialManagerWeeklyRevenueRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('get_manager_partner_weekly_revenue', {
      p_manager_user_id: managerUserId,
      p_period_id: periodId ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as TerritorialManagerWeeklyRevenueRow[]
}

export async function getStudioWeeklyPlanFactRows(params: {
  studioIds: number[]
  periodId: number
  weekNumber?: number | null
}): Promise<StudioWeeklyPlanFactRow[]> {
  if (params.studioIds.length === 0) return []
  const supabase = getSupabase()
  const studioIds = Array.from(new Set(params.studioIds))
  let query = supabase
    .from('studio_weekly_plan_vs_fact')
    .select(`
      studio_internal_id,
      period_id,
      week_id,
      week_number_in_period,
      week_start,
      week_end,
      weekly_gross_revenue_plan,
      weekly_gross_revenue_fact,
      weekly_marketing_budget_plan,
      weekly_marketing_expenses_amount_fact,
      weekly_leads_plan,
      weekly_leads_fact,
      weekly_appointments_plan,
      weekly_appointments_fact,
      weekly_new_visitors_plan,
      weekly_new_visitors_fact,
      weekly_new_sales_plan,
      weekly_new_sales_fact,
      weekly_new_sales_amount_plan,
      weekly_new_sales_amount_fact,
      weekly_renewals_count_plan,
      weekly_renewals_count_fact,
      weekly_renewals_amount_plan,
      weekly_renewals_amount_fact,
      weekly_upgrades_count_plan,
      weekly_upgrades_count_fact,
      weekly_upgrades_amount_plan,
      weekly_upgrades_amount_fact,
      weekly_returning_clients_count_plan,
      weekly_returning_clients_count_fact,
      weekly_returning_clients_amount_plan,
      weekly_returning_clients_amount_fact,
      weekly_membership_fees_new_plan,
      weekly_membership_fees_new_amount_fact,
      weekly_membership_fees_subscription_upgrades_plan,
      weekly_membership_fees_subscription_returning_plan,
      weekly_membership_fees_subscription_upgrades_amount_fact,
      weekly_membership_fees_subscription_returning_amount_fact,
      weekly_new_subscriptions_count_plan,
      weekly_new_subscriptions_count_fact,
      weekly_new_subscriptions_amount_plan,
      weekly_new_subscriptions_amount_fact,
      weekly_subscription_renewals_count_plan,
      weekly_subscription_renewals_count_fact,
      weekly_subscription_renewals_amount_plan,
      weekly_subscription_renewals_amount_fact,
      weekly_subscription_upgrades_count_plan,
      weekly_subscription_upgrades_count_fact,
      weekly_subscription_upgrades_amount_plan,
      weekly_subscription_upgrades_amount_fact,
      weekly_subscription_returning_clients_count_plan,
      weekly_subscription_returning_clients_count_fact,
      weekly_subscription_returning_clients_amount_plan,
      weekly_subscription_returning_clients_amount_fact,
      weekly_refunds_amount_plan,
      weekly_refunds_amount_fact
    `)
    .in('studio_internal_id', studioIds)
    .eq('period_id', params.periodId)

  if (params.weekNumber != null) {
    query = query.eq('week_number_in_period', params.weekNumber)
  }

  const { data, error } = await withAuthRecovery(() => query.order('week_number_in_period', { ascending: true })) as any
  if (error) throw new Error(formatError(error))
  return (data ?? []) as StudioWeeklyPlanFactRow[]
}

export async function getStudioDailyCallsRows(params: {
  studioIds: number[]
  dateFrom: string
  dateTo: string
}): Promise<StudioDailyCallsRow[]> {
  if (params.studioIds.length === 0) return []
  const supabase = getSupabase()
  const studioIds = Array.from(new Set(params.studioIds))
  const { data, error } = await withAuthRecovery(() =>
    supabase
      .from('studio_daily_totals')
      .select('studio_internal_id, metric_date, calls_count, answered_calls_count')
      .in('studio_internal_id', studioIds)
      .gte('metric_date', params.dateFrom)
      .lte('metric_date', params.dateTo)
      .order('metric_date', { ascending: true })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as StudioDailyCallsRow[]
}

// Mutations (advanced MC only — enforced server-side)

export async function assignTerritorialManager(params: {
  managerUserId: string
  partnerNumber: number
  assignedFrom?: string  // ISO date; defaults to today on server
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('assign_territorial_manager', {
      p_manager_user_id: params.managerUserId,
      p_partner_number: params.partnerNumber,
      p_assigned_from: params.assignedFrom ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

/**
 * Снимает партнёра с ТУ.
 * `unassignedAt` — ДАТА СНЯТИЯ: первый день, когда партнёр за ТУ уже не числится.
 * В `assigned_to` сервер положит `unassignedAt - 1` (последний день работы).
 * Дата раньше начала назначения удаляет строку; уже снятое назначение переоформляется.
 */
export async function unassignTerritorialManager(params: {
  assignmentId: number
  unassignedAt?: string
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('unassign_territorial_manager', {
      p_assignment_id: params.assignmentId,
      p_unassigned_at: params.unassignedAt ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

/**
 * Снятые партнёры этого ТУ. Витрина активных назначений их по определению не
 * показывает, поэтому без этого запроса ошибочную дату снятия нечем исправить.
 */
export async function listManagerClosedAssignments(
  managerUserId: string
): Promise<TerritorialManagerClosedAssignment[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_manager_closed_assignments', {
      p_manager_user_id: managerUserId,
      p_limit: 200,
    })
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as TerritorialManagerClosedAssignment[]
}

export async function listMcUsersForTuAdmin(): Promise<McUserForTuAdmin[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_mc_users_for_tu_admin')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as McUserForTuAdmin[]
}

export async function setUserTerritorialManager(params: {
  userId: string
  isTm: boolean
  effectiveAt?: string | null
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_user_territorial_manager', {
      p_user_id: params.userId,
      p_is_tm: params.isTm,
      p_effective_at: params.effectiveAt ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}

/** Итог (или предпросмотр) снятия с должности ТУ. */
export type TuRemovalResult = {
  last_day: string
  closed: number
  deleted: number
  deleted_list: string[]
  conflict: string | null
  conflict_hint: string | null
}

/**
 * Снять с должности ТУ или ИЗМЕНИТЬ уже поставленную дату.
 * `lastDay` — ПОСЛЕДНИЙ день в должности, включительно («снят с 30.09» = 30.09 ещё его,
 * нового ТУ ставят с 01.10). Может быть в прошлом, сегодня или в будущем. Назначения,
 * закрытые прежней датой, следуют за новой. `dryRun` ничего не пишет — предпросмотр.
 */
export async function setTerritorialManagerLastDay(params: {
  userId: string
  lastDay: string
  dryRun?: boolean
}): Promise<TuRemovalResult> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('set_territorial_manager_last_day', {
      p_user_id: params.userId,
      p_last_day: params.lastDay,
      p_dry_run: params.dryRun ?? false,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as TuRemovalResult
}

/** Снятые с должности ТУ — чтобы их карточка открывалась и дату снятия можно было поправить. */
export async function listFormerTerritorialManagers(): Promise<TerritorialManagerListItem[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('list_former_territorial_managers')
  )
  if (error) throw new Error(formatError(error))
  return (data ?? []) as TerritorialManagerListItem[]
}

export async function cancelScheduledTuRemoval(userId: string): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('cancel_scheduled_tu_removal', { p_user_id: userId })
  )
  if (error) throw new Error(formatError(error))
}

export async function cancelScheduledAssignmentClose(assignmentId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('cancel_scheduled_assignment_close', { p_assignment_id: assignmentId })
  )
  if (error) throw new Error(formatError(error))
}

export async function transferTerritorialManager(params: {
  partnerNumber: number
  newManagerUserId: string
  effectiveFrom?: string
  note?: string | null
}): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase as any).rpc('transfer_territorial_manager', {
      p_partner_number: params.partnerNumber,
      p_new_manager_user_id: params.newManagerUserId,
      p_effective_from: params.effectiveFrom ?? null,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return data as number
}

export async function updateTerritorialManagerAssignmentStart(params: {
  assignmentId: number
  assignedFrom: string
  note?: string | null
}): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase as any).rpc('update_territorial_manager_assignment_start', {
      p_assignment_id: params.assignmentId,
      p_assigned_from: params.assignedFrom,
      p_note: params.note ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
}
